// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The shell, and everything that draws into it.
 *
 * One linear workflow — drop, confirm, scan, search, export — so the page is
 * four sections and exactly one of the middle ones is visible at a time. There
 * are no tabs and no settings panel; the three controls that shape the results
 * sit next to the results they shape, because the whole argument of this tool
 * is that you should be adjusting them while looking at real frames.
 */

import { term } from './glossary.ts';
import { formatDuration, timecode } from './core/sampling.ts';
import { STANDOUT_FLOOR, median, standout } from './core/moments.ts';
import type { Moment, VideoFacts } from './types.ts';

export const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
};

const icon = (path: string, size = 18): string =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;

const ICON_MARK =
  '<path d="M3 12h4M17 12h4"/><path d="M12 4v16"/><circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/>';
const ICON_FILM = '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 4v16M17 4v16M3 12h18"/>';
const ICON_SEARCH = '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>';
const ICON_DOWN = '<path d="M12 3v12"/><path d="m7 11 5 5 5-5"/><path d="M4 21h16"/>';
const ICON_SHIELD = '<path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z"/><path d="m9 12 2 2 4-4"/>';

export function renderShell(): void {
  el('app').innerHTML = `
    <header class="site-header">
      <span class="brand">${icon(ICON_MARK, 22)} cuepoint</span>
      <nav class="header-nav" aria-label="About this tool">
        <button class="nav-btn" id="nav-how" type="button">How it works</button>
        <button class="nav-btn" id="nav-privacy" type="button">Privacy</button>
        <button class="nav-btn" id="nav-about" type="button">About</button>
        <button class="nav-btn" id="log-toggle" type="button" aria-expanded="false">
          Log <span class="log-count" id="log-count">0</span>
        </button>
      </nav>
    </header>

    <main class="main-content">
      <section class="intro">
        <h1>Find the moment in a long video by describing it.</h1>
        <p class="lede">
          Drop in an hours-long recording — a dashcam card, a day of security footage, raw footage
          you need to log — type what you are looking for, and cut the matching moments out as
          clips. The file never leaves your computer.
        </p>
        <button class="trust-badge" id="trust-badge" type="button">
          ${icon(ICON_SHIELD, 16)} Runs entirely in your browser. Nothing is uploaded — read how
        </button>
      </section>

      <section id="stage-drop">
        <div class="dropzone" id="dropzone" role="button" tabindex="0"
             aria-label="Choose a video file, or drop one here">
          ${icon(ICON_FILM, 34)}
          <p class="dz-title">Drop a video here</p>
          <p class="dz-hint">or tap to choose one · MP4, MOV, WebM, MKV · any length</p>
          <p class="dz-privacy">It is read from your disk and decoded in this tab. It is not uploaded.</p>
        </div>
        <input type="file" id="file-input" accept="video/*,.mp4,.mov,.m4v,.webm,.mkv" hidden />
        <div class="samples">
          <span class="samples-label">Never used it before?</span>
          <button class="sample-pill" id="sample-btn" type="button">
            Try the demo reel <span class="pill-tag">85 s · 1.7 MB</span>
          </button>
        </div>
        <p class="scope-note">
          cuepoint finds <strong>things and places</strong>, not actions — ${term('scene', 'a red car, a bicycle, a wet road at night')},
          rather than “a car turning left”. It also cannot tell you something is <em>absent</em>.
          <button class="linklike" id="limits-link" type="button">What it can and cannot do</button>
        </p>
      </section>

      <section id="stage-facts" hidden></section>

      <section id="stage-scan" hidden>
        <div class="progress-panel">
          <div class="progress-row">
            <span class="progress-phase" id="scan-phase">Starting…</span>
            <span class="progress-count" id="scan-count"></span>
          </div>
          <div class="progress-track"><div class="progress-fill" id="scan-fill"></div></div>
          <div class="progress-row progress-sub">
            <span id="scan-rate"></span>
            <span id="scan-eta"></span>
          </div>
          <div class="action-row">
            <button class="btn btn-sm" id="scan-cancel" type="button">Stop</button>
          </div>
        </div>
      </section>

      <section id="stage-search" hidden>
        <div class="search-head">
          <label class="search-box">
            ${icon(ICON_SEARCH, 18)}
            <input type="search" id="query" placeholder="a red car" autocomplete="off"
                   spellcheck="false" aria-label="Describe what you are looking for" />
          </label>
          <div class="chips" id="chips"></div>
        </div>
        <div id="results"></div>
        <div class="panel" id="export-panel" hidden>
          <div class="panel-head"><h2 class="panel-title">Take it with you</h2></div>
          <div class="panel-body action-row" id="export-row"></div>
        </div>
      </section>

      <section id="error-panel" class="error-panel" hidden></section>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      </div>
    </footer>
  `;
}

const fileSize = (bytes: number): string =>
  bytes >= 1e9 ? `${(bytes / 1e9).toFixed(2)} GB` : `${(bytes / 1e6).toFixed(1)} MB`;

/**
 * What the container said, and what a scan will therefore cost.
 *
 * Shown before anything is decoded and before the model is fetched, because
 * "this will take eleven minutes and download 65 MB" is a decision the user
 * gets to make rather than discover.
 */
export function renderFacts(
  facts: VideoFacts,
  plan: { frames: number; onKeyframe: number; keyframeSpacing: number | null },
  modelBytes: number,
  rate: number,
): string {
  const spacing = plan.keyframeSpacing;
  return `
    <div class="panel">
      <div class="panel-head">
        <h2 class="panel-title">${escapeHtml(facts.name)}</h2>
        <span class="panel-meta">${fileSize(facts.bytes)}</span>
      </div>
      <div class="panel-body">
        <dl class="facts">
          <div><dt>Length</dt><dd>${formatDuration(facts.duration)}</dd></div>
          <div><dt>Picture</dt><dd>${facts.width}×${facts.height}</dd></div>
          <div><dt>Codec</dt><dd>${escapeHtml(facts.codec)}</dd></div>
          <div><dt>${term('keyframe', 'Keyframes')}</dt><dd>${facts.keyframes.length.toLocaleString()}${
            spacing ? ` · one every ${spacing < 1 ? `${(spacing * 1000).toFixed(0)} ms` : `${spacing.toFixed(1)} s`}` : ''
          }</dd></div>
        </dl>

        <div class="rate-row">
          <label for="rate">Look at</label>
          <select id="rate">
            <option value="0.25"${rate === 0.25 ? ' selected' : ''}>1 frame every 4 seconds</option>
            <option value="0.5"${rate === 0.5 ? ' selected' : ''}>1 frame every 2 seconds</option>
            <option value="1"${rate === 1 ? ' selected' : ''}>1 frame a second</option>
            <option value="2"${rate === 2 ? ' selected' : ''}>2 frames a second</option>
          </select>
        </div>

        <p class="plan-line" id="plan-line">
          <strong>${plan.frames.toLocaleString()} frames</strong> to look at,
          ${plan.onKeyframe.toLocaleString()} of them already stored whole.
          ${modelBytes > 0
            ? `The model is downloaded once (about <strong>${(modelBytes / 1e6).toFixed(0)} MB</strong>, a little more if
               your browser has to fall back to software) and then kept for next time.`
            : 'The model is already on this machine.'}
        </p>
        <p class="plan-eta" id="plan-eta">Measuring how fast this machine is…</p>

        <div class="action-row">
          <button class="btn btn-primary" id="start-scan" type="button">Scan this video</button>
          <button class="btn" id="pick-other" type="button">Choose a different file</button>
        </div>
      </div>
    </div>
  `;
}

export interface ResultView {
  readonly moment: Moment;
  readonly thumb: ImageBitmap | null;
}

/**
 * The results, and the distribution they came out of.
 *
 * The distribution is not decoration and is not optional. A ranked list is
 * returned whether or not the thing being searched for is anywhere in the video,
 * and a CSV of timestamps reads like a finding rather than a guess — so the
 * spread of every frame's score, with this video's own baseline drawn on it, is
 * shown right beside the results. When the best result sits on top of the
 * baseline, that is visible at a glance and it is stated in words as well.
 */
export function renderResults(
  container: HTMLElement,
  query: string,
  results: readonly ResultView[],
  scores: Float32Array,
  duration: number,
  onPick: (moment: Moment) => void,
): void {
  container.replaceChildren();
  if (!query.trim()) {
    container.innerHTML = `<p class="empty">Type something you would see in a single frame — <em>a red car</em>,
      <em>a bicycle</em>, <em>a kitchen</em>, <em>city lights at night</em>.</p>`;
    return;
  }
  if (results.length === 0) {
    container.innerHTML = `<p class="empty">No frames were scanned yet.</p>`;
    return;
  }

  const base = median(scores);
  const top = results[0]!.moment.peakScore;
  const z = standout(scores, top);
  const weak = z < STANDOUT_FLOOR;

  const verdict = document.createElement('div');
  verdict.className = 'verdict';
  verdict.innerHTML = `
    <p class="verdict-headline">${results.length} moment${results.length === 1 ? '' : 's'} look
      most like “${escapeHtml(query)}”.</p>
    <p class="verdict-sub">
      ${
        weak
          ? `<strong>Read this cautiously.</strong> The best frame stands only
             <span class="mono">${z.toFixed(1)}×</span> the usual spread above a typical frame of this video,
             which normally means nothing here stood out. It does <em>not</em> mean the thing is absent —
             cuepoint cannot tell you that, and no setting makes it able to.`
          : `The best frame stands <span class="mono">${z.toFixed(1)}×</span> the usual spread above a typical
             frame of this video, which is a real separation. cuepoint still cannot tell you that something is
             <em>not</em> present.`
      }
      <button class="linklike" id="limits-link-2" type="button">What it can and cannot do</button>
    </p>
    ${distribution(scores, base, top)}
  `;
  container.append(verdict);

  const list = document.createElement('div');
  list.className = 'moment-list';
  results.forEach((view, i) => {
    const card = document.createElement('article');
    card.className = 'moment';
    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'moment-thumb';
    if (view.thumb) {
      const canvas = document.createElement('canvas');
      canvas.width = view.thumb.width;
      canvas.height = view.thumb.height;
      canvas.getContext('2d')?.drawImage(view.thumb, 0, 0);
      thumbWrap.append(canvas);
    } else {
      thumbWrap.classList.add('is-pending');
    }
    const body = document.createElement('div');
    body.className = 'moment-body';
    body.innerHTML = `
      <p class="moment-time">${timecode(view.moment.start)} – ${timecode(view.moment.end)}</p>
      <p class="moment-meta">#${i + 1} · best frame at ${timecode(view.moment.peakTime)} ·
        <span class="mono">${view.moment.peakScore.toFixed(3)}</span></p>
    `;
    const actions = document.createElement('div');
    actions.className = 'moment-actions';
    const cut = document.createElement('button');
    cut.className = 'btn btn-sm btn-primary';
    cut.type = 'button';
    cut.innerHTML = `${icon(ICON_DOWN, 15)} Cut this clip`;
    cut.addEventListener('click', () => onPick(view.moment));
    actions.append(cut);
    body.append(actions);
    card.append(thumbWrap, body);
    list.append(card);
  });
  container.append(list);

  const bar = document.createElement('p');
  bar.className = 'timeline-note';
  bar.textContent = `Across ${formatDuration(duration)} of video, ${scores.length.toLocaleString()} frames were compared.`;
  container.append(bar);
}

/**
 * A histogram of every frame's score, with the baseline and the top result
 * marked. Drawn as inline SVG rather than a canvas so it stays crisp, scales
 * with the layout, and is legible to a screen reader through its label.
 */
function distribution(scores: Float32Array, base: number, top: number): string {
  const bins = 40;
  const lo = Math.min(...scores);
  const hi = Math.max(...scores);
  const span = hi - lo || 1;
  const counts = new Array<number>(bins).fill(0);
  for (const s of scores) {
    const b = Math.min(bins - 1, Math.max(0, Math.floor(((s - lo) / span) * bins)));
    counts[b] = (counts[b] ?? 0) + 1;
  }
  const peak = Math.max(...counts, 1);
  const w = 100 / bins;
  const bars = counts
    .map((c, i) => {
      const h = (c / peak) * 100;
      return `<rect x="${(i * w).toFixed(3)}" y="${(100 - h).toFixed(2)}" width="${(w * 0.82).toFixed(3)}" height="${h.toFixed(2)}" />`;
    })
    .join('');
  const at = (v: number) => (((v - lo) / span) * 100).toFixed(2);
  return `
    <figure class="dist">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img"
           aria-label="Distribution of similarity scores across every scanned frame, with the typical frame and the best match marked.">
        <g class="dist-bars">${bars}</g>
        <line class="dist-base" x1="${at(base)}" x2="${at(base)}" y1="0" y2="100" />
        <line class="dist-top" x1="${at(top)}" x2="${at(top)}" y1="0" y2="100" />
      </svg>
      <figcaption>
        <span class="key key-base">typical frame (${term('baseline', 'baseline')})</span>
        <span class="key key-top">best match</span>
      </figcaption>
    </figure>
  `;
}

export function showError(message: string, retry?: () => void): void {
  const panel = el('error-panel');
  panel.innerHTML = `
    <p class="error-title">${icon('<circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/>', 18)} That did not work</p>
    <p class="error-detail">${escapeHtml(message)}</p>
  `;
  if (retry) {
    const row = document.createElement('div');
    row.className = 'action-row';
    const button = document.createElement('button');
    button.className = 'btn btn-primary';
    button.type = 'button';
    button.textContent = 'Try again';
    button.addEventListener('click', () => {
      panel.hidden = true;
      retry();
    });
    row.append(button);
    panel.append(row);
  }
  panel.hidden = false;
}

export function clearError(): void {
  el('error-panel').hidden = true;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
